import { Box, Text } from "@primer/react";
import { AlertIcon, CheckCircleIcon, ClockIcon } from "@primer/octicons-react";
import { useAppSelector } from "../../hooks/useAppSelector";

/**
 * Panel công nợ học phí — chỉ hiện khi đợt bật cờ check_tai_chinh.
 * Thay cho việc công nợ chỉ hiện khi hover nút đăng ký: panel này luôn hiển thị rõ ràng.
 *
 * Render rule:
 *   - Cờ check_tai_chinh off / data null → return null.
 *   - Nợ trong mức cho phép (no_tai_chinh ≤ so_tien_no_toi_da) → return null (chỉ hiện khi vượt ngưỡng).
 *   - no_tai_chinh > so_tien_no_toi_da (vuotNo): panel ĐỎ — chặn đăng ký, yêu cầu nộp học phí.
 *
 * Dữ liệu từ cùng nguồn FinancialSummaryWidget (financial state, poll 60s + refresh sau register/cancel).
 */
const DebtNoticeWidget = () => {
    const { data } = useAppSelector(s => s.financial);
    if (!data || !data.check_tai_chinh) return null;

    const no = data.no_tai_chinh ?? 0;
    const nguong = data.so_tien_no_toi_da ?? 0;
    const vuotNo = no > nguong;
    // Chỉ hiển thị khi SV nợ học phí VƯỢT ngưỡng cho phép — trong mức cho phép thì ẩn hẳn panel.
    if (!vuotNo) return null;

    let updatedAtText = "";
    if (data.updated_at) {
        const d = new Date(data.updated_at);
        updatedAtText = d.toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" });
    }

    const palette = vuotNo
        ? { bg: "#fef2f2", border: "#fecaca", barBg: "#fef2f2", icon: "#991b1b", headerTxt: "#991b1b", accent: "#b91c1c" }
        : { bg: "#ffffff", border: "#d6e9d6", barBg: "#f0fdf4", icon: "#166534", headerTxt: "#166534", accent: "#15803d" };

    const headerLabel = vuotNo
        ? "Bạn đang nợ học phí vượt mức — không thể đăng ký"
        : "Công nợ học phí";

    return (
        <Box
            sx={{
                bg: palette.bg,
                border: `1px solid ${palette.border}`,
                borderRadius: 8,
                mb: 3,
                overflow: "hidden",
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
            }}
        >
            {/* Header bar */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    rowGap: 1,
                    columnGap: 3,
                    bg: palette.barBg,
                    borderBottom: `1px solid ${palette.border}`,
                    px: [2, 3],
                    py: "8px",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
                    <Box sx={{ color: palette.icon, display: "flex", flexShrink: 0 }}>
                        {vuotNo ? <AlertIcon size={16} /> : <CheckCircleIcon size={16} />}
                    </Box>
                    <Text sx={{ fontWeight: 700, fontSize: [0, 1], color: palette.headerTxt, letterSpacing: "0.2px" }}>
                        {headerLabel}
                    </Text>
                </Box>
                {updatedAtText && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#6b7280", fontSize: 0, fontStyle: "italic" }}>
                        <ClockIcon size={12} />
                        <Text>Cập nhật {updatedAtText}</Text>
                    </Box>
                )}
            </Box>

            {/* Stat tiles */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: ["repeat(2, 1fr)", "repeat(2, 1fr)", "repeat(auto-fit, minmax(180px, 1fr))"],
                    bg: "#ffffff",
                }}
            >
                <Tile
                    label="Đang nợ học phí"
                    value={`${no.toLocaleString("vi-VN")}đ`}
                    valueColor={vuotNo ? "#b91c1c" : "#111827"}
                    strong
                />
                <Tile
                    label="Mức nợ cho phép"
                    value={`${nguong.toLocaleString("vi-VN")}đ`}
                    valueColor="#374151"
                />
            </Box>

            {/* Status line */}
            <Box
                sx={{
                    px: [2, 3],
                    py: "8px",
                    borderTop: "1px solid #f0f1f3",
                    color: vuotNo ? "#991b1b" : "#166534",
                    fontSize: 0,
                }}
            >
                {vuotNo
                    ? `Vượt mức cho phép ${(no - nguong).toLocaleString("vi-VN")}đ. Vui lòng nộp học phí trước khi đăng ký.`
                    : "Công nợ trong mức cho phép — bạn được đăng ký bình thường."}
            </Box>
        </Box>
    );
};

const Tile = ({
    label,
    value,
    valueColor,
    strong,
}: {
    label: string;
    value: string;
    valueColor: string;
    strong?: boolean;
}) => (
    <Box
        sx={{
            px: [2, 3],
            py: ["8px", "10px"],
            borderRight: "1px solid",
            borderBottom: "1px solid",
            borderColor: "#f0f1f3",
        }}
    >
        <Text
            sx={{
                display: "block",
                fontSize: 0,
                color: "#6b7280",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.4px",
                mb: "2px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
            }}
        >
            {label}
        </Text>
        <Text
            sx={{
                display: "block",
                fontSize: strong ? [2, 3] : [1, 2],
                fontWeight: 700,
                color: valueColor,
                fontVariantNumeric: "tabular-nums",
                lineHeight: 1.25,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
            }}
        >
            {value}
        </Text>
    </Box>
);

export default DebtNoticeWidget;
